import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import AdminVATSettingsForm from '../components/AdminVATSettingsForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';

const API_PREFIX = '/internal/vat-settings';

export default function AdminVATSettings() {
  const [vatSettings, setVatSettings] = useState([]);
  const [filteredSettings, setFilteredSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const { showToast } = useToast();

  const fetchVATSettings = useCallback(async () => {
    try {
      setLoading(true);
      const params = showInactive ? '?activeOnly=false' : '';
      const response = await api.get(`${API_PREFIX}${params}`);
      setVatSettings(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch VAT settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchVATSettings();
  }, [fetchVATSettings]);

  useEffect(() => {
    let filtered = vatSettings;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.country?.toLowerCase().includes(term) ||
          s.countryCode?.toLowerCase().includes(term)
      );
    }
    setFilteredSettings(filtered);
  }, [vatSettings, searchTerm]);

  const handleAdd = () => {
    setEditingSetting(null);
    setShowForm(true);
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setShowForm(true);
  };

  const handleDelete = (setting) => {
    setDeleteTarget(setting);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${API_PREFIX}/${deleteTarget._id}`);
      showToast(`VAT setting for "${deleteTarget.country}" deactivated`, 'success');
      setDeleteTarget(null);
      fetchVATSettings();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete VAT setting', 'error');
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingSetting;
    if (isEditing) {
      await api.put(`${API_PREFIX}/${editingSetting._id}`, data);
    } else {
      await api.post(API_PREFIX, data);
    }
    setShowForm(false);
    setEditingSetting(null);
    fetchVATSettings();
    showToast(
      isEditing ? 'VAT setting updated successfully!' : 'VAT setting created successfully!',
      'success'
    );
  };

  const columns = [
    { key: 'country', label: 'Country', sortable: true },
    { key: 'countryCode', label: 'Code', sortable: true },
    {
      key: 'rate',
      label: 'VAT Rate',
      sortable: true,
      render: (val) => `${val}%`,
    },
    {
      key: 'isActive',
      label: 'Status',
      sortable: true,
      render: (val) =>
        val ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Inactive
          </span>
        ),
    },
  ];

  const renderFilters = () => (
    <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
      <input
        type="text"
        placeholder="Search by country or code..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ flex: '1 1 250px' }}
      />
      <label className="flex items-center space-x-2 cursor-pointer text-sm text-slate-700">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
        />
        <span>Show inactive</span>
      </label>
    </div>
  );

  if (loading && vatSettings.length === 0) return <div>Loading...</div>;
  if (error && vatSettings.length === 0) return <div className="error">{error}</div>;

  return (
    <div>
      {showForm ? (
        <AdminVATSettingsForm
          vatSetting={editingSetting}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingSetting(null);
          }}
        />
      ) : (
        <DataTable
          title="VAT Settings"
          data={filteredSettings}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
          renderFilters={renderFilters}
        />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Deactivate VAT Setting"
          message={`Are you sure you want to deactivate the VAT setting for "${deleteTarget.country}" (${deleteTarget.countryCode})?`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          confirmText="Deactivate"
        />
      )}
    </div>
  );
}
